import React from "react";
import { Link } from "react-router-dom";
import InnerHero from "../../components/InnerHero";
import {
  ArrowLeft,
  CheckCircle2,
  ShoppingBag,
  BookOpen,
  Building2,
  Info,
} from "lucide-react";

const dashboard = [
  ["1959", "Registered Act"],
  ["Section 7", "Registration"],
  ["Students", "Primary Users"],
  ["Daily", "Essential Services"],
];

const registrationDetails = [
  ["Registration", "Registered under Co-operative Society Act, 1959"],
  ["Section", "Section 7"],
  ["Registration No.", "19/RSR/32603/RGN/07-09"],
  ["Facility", "VDIT Students Consumers Co-operative Stores"],
];

const facilities = [
  "Stationery items",
  "Cutlery items",
  "Cosmetics",
  "Uniform dress materials",
  "Educational aids",
  "Daily student utility products",
];

const benefits = [
  "One-stop store for student requirements.",
  "Affordable educational materials.",
  "Easy availability of daily-use products.",
  "Convenient campus location.",
  "Supports students throughout their academic journey.",
];

const quickFacts = [
  ["Facility", "Co-operative Society"],
  ["Registered Under", "Co-operative Society Act, 1959"],
  ["Section", "7"],
  ["Registration No.", "19/RSR/32603/RGN/07-09"],
  ["Beneficiaries", "Students"],
  ["Purpose", "Student Consumer Store"],
];

const CooperativeSociety = () => {
  return (
    <main>
      <InnerHero
        title="Co-operative Society"
        image="/Coop_images/CoopHero.png"
        breadcrumbs={[
          { label: "Campus Life", to: "/campus" },
          { label: "Co-operative Society" },
        ]}
      />

      <section className="bg-surface-alt/60 border-b border-brand/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3">
          <Link
            to="/campus"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand font-semibold hover:underline"
          >
            <ArrowLeft size={14} />
            Back to Campus Life
          </Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="grid lg:grid-cols-3 gap-10">

          {/* Main Content */}

          <div className="lg:col-span-2 space-y-10">

            <section className="bg-white border border-brand/15 p-7 md:p-9">

              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 border border-brand/20 flex items-center justify-center text-brand">
                  <ShoppingBag size={22} />
                </div>

                <p className="text-xs uppercase tracking-[0.24em] text-brand font-semibold">
                  Student Consumer Store
                </p>
              </div>

              <h2
                className="text-3xl md:text-5xl text-brand font-semibold"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Co-operative Society
              </h2>

              <p className="mt-5 text-[16px] text-[#2a2a2a]/85 leading-relaxed">
                The VDIT Students Consumers Co-operative Stores caters to the
                day-to-day needs of students by providing stationery, cutlery,
                cosmetics, uniform dress materials and educational aids. The
                society functions as a convenient on-campus store where students
                can obtain essential academic and personal utility items.
              </p>

              <p className="mt-4 text-[16px] text-[#2a2a2a]/85 leading-relaxed">
                Registered under the Co-operative Society Act, 1959 (Section 7),
                the Co-operative Society serves as a reliable support facility,
                ensuring students have easy access to the materials they require
                throughout their academic journey.
              </p>

            </section>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">

              {dashboard.map(([value, label]) => (

                <div
                  key={label}
                  className="bg-[#7a1d2c] text-white p-5"
                >
                  <p
                    className="text-3xl font-semibold"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {value}
                  </p>

                  <p className="text-xs uppercase tracking-widest mt-2 text-white/80">
                    {label}
                  </p>

                </div>

              ))}

            </div>

            <section className="bg-white border border-brand/15 p-7">

              <h3
                className="text-2xl text-brand font-semibold flex items-center gap-2 mb-5"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                <Info size={22} />
                Registration Details
              </h3>

              <table className="w-full border-collapse text-sm">

                <tbody>

                  {registrationDetails.map(([label, value]) => (

                    <tr key={label}>

                      <td className="border border-brand/20 px-4 py-3 font-semibold bg-surface-alt/40">
                        {label}
                      </td>

                      <td className="border border-brand/20 px-4 py-3">
                        {value}
                      </td>

                    </tr>

                  ))}

                </tbody>

              </table>

            </section>

            <section className="grid md:grid-cols-2 gap-6">

              <div className="bg-white border border-brand/15 p-6">

                <h3
                  className="text-2xl text-brand font-semibold flex items-center gap-2 mb-4"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  <BookOpen size={20} />
                  Products & Services
                </h3>

                <ul className="space-y-3">

                  {facilities.map((item) => (

                    <li
                      key={item}
                      className="flex items-start gap-3"
                    >
                      <CheckCircle2
                        size={18}
                        className="text-brand mt-0.5 flex-shrink-0"
                      />

                      <span>{item}</span>

                    </li>

                  ))}

                </ul>

              </div>

              <div className="bg-white border border-brand/15 p-6">

                <h3
                  className="text-2xl text-brand font-semibold flex items-center gap-2 mb-4"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  <Building2 size={20} />
                  Student Benefits
                </h3>

                <ul className="space-y-3">

                  {benefits.map((item) => (

                    <li
                      key={item}
                      className="flex items-start gap-3"
                    >
                      <CheckCircle2
                        size={18}
                        className="text-brand mt-0.5 flex-shrink-0"
                      />

                      <span>{item}</span>

                    </li>

                  ))}

                </ul>

              </div>

            </section>

            <section className="bg-white border border-brand/15 p-7">

              <h3
                className="text-2xl text-brand font-semibold mb-5"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                VDIT Students Consumers Co-operative Store
              </h3>

              <img
                src="/Coop_images/Coop.png"
                alt="Co-operative Society"
                className="w-full rounded border border-brand/10"
              />

            </section>

          </div>

          {/* Sidebar */}

          <aside className="bg-white border border-brand/15 p-5 lg:sticky lg:top-36 self-start">

            <h4 className="text-xs uppercase tracking-widest text-brand font-semibold">
              Quick Facts
            </h4>

            <div className="mt-4 space-y-3 text-sm">

              {quickFacts.map(([label, value]) => (

                <div key={label}>

                  <p className="text-[#2a2a2a]/70">
                    {label}
                  </p>

                  <p className="font-semibold">
                    {value}
                  </p>

                </div>

              ))}

            </div>

          </aside>

        </div>
      </section>
    </main>
  );
};

export default CooperativeSociety;
